import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Shield, Lock, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle, XCircle } from "lucide-react";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || "Lien invalide ou expiré");
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success("Mot de passe modifié !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF5F7] to-[#FFF0F5] flex flex-col items-center justify-center p-4">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center shadow-lg">
            <Shield size={28} className="text-[#FF3366]" fill="#FF3366" />
          </div>
          <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            SHARE
          </span>
        </div>
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <Loader2 className="animate-spin text-[#FF3366] mx-auto" size={48} />
            <p className="mt-4 text-slate-600">Vérification du lien...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF5F7] to-[#FFF0F5] flex flex-col items-center justify-center p-4">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center shadow-lg">
            <Shield size={28} className="text-[#FF3366]" fill="#FF3366" />
          </div>
          <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            SHARE
          </span>
        </div>
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="invalid-token-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide ou expiré</h2>
            <p className="text-slate-600 mb-6">
              Ce lien de réinitialisation n'est plus valide. Il a peut-être expiré ou déjà été utilisé.
            </p>
            <div className="space-y-2">
              <Link to="/forgot-password">
                <Button className="w-full btn-guardian rounded-xl">
                  Demander un nouveau lien
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="w-full rounded-xl">
                  <ArrowLeft size={16} className="mr-2" />
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF5F7] to-[#FFF0F5] flex flex-col items-center justify-center p-4">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center shadow-lg">
            <Shield size={28} className="text-[#FF3366]" fill="#FF3366" />
          </div>
          <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            SHARE
          </span>
        </div>
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="success-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Mot de passe modifié !</h2>
            <p className="text-slate-600 mb-6">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <Link to="/auth">
              <Button className="w-full btn-guardian rounded-xl h-12">
                Se connecter
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F7] to-[#FFF0F5] flex flex-col items-center justify-center p-4">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center shadow-lg">
          <Shield size={28} className="text-[#FF3366]" fill="#FF3366" />
        </div>
        <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          SHARE
        </span>
      </div>

      <Card className="w-full max-w-md shadow-xl border-0" data-testid="reset-password-card">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl font-bold">Nouveau mot de passe</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className="text-slate-600 text-sm text-center mb-4">
            Créez un nouveau mot de passe pour <strong>{tokenEmail}</strong>
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 rounded-xl h-12"
                  data-testid="new-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-slate-500">Minimum 6 caractères</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-bold">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-10 pr-10 rounded-xl h-12"
                  data-testid="confirm-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-guardian rounded-xl h-12 text-base"
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Modification en cours...
                </>
              ) : (
                "Modifier le mot de passe"
              )}
            </Button>

            <div className="text-center pt-2">
              <Link 
                to="/auth" 
                className="text-[#FF3366] hover:underline text-sm inline-flex items-center"
              >
                <ArrowLeft size={14} className="mr-1" />
                Retour à la connexion
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>

      <p className="mt-6 text-slate-500 text-sm">
        © 2026 RATE⭐SHARE - Le GPS de la Bienveillance
      </p>
    </div>
  );
};

export default ResetPasswordPage;
